import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Table, Button, Alert, Card, Badge, Spinner } from 'react-bootstrap';
import { vacanciesAPI } from '../services/api';

interface Vacancy {
  id: number;
  title: string;
  closing_date: string;
  created_at: string;
  documents: any[];
}

const Vacancies: React.FC = () => {
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadVacancies();
  }, []);

  const loadVacancies = async () => {
    try {
      setLoading(true);
      const response = await vacanciesAPI.getAll();
      setVacancies(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load vacancies');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this vacancy?')) {
      try {
        await vacanciesAPI.delete(String(id));
        loadVacancies();
      } catch (err) {
        setError('Failed to delete vacancy');
      }
    }
  };

  const formatDate = (date: string) => {
    return date ? new Date(date).toLocaleDateString() : '-';
  };

  const isClosingSoon = (closingDate: string) => {
    if (!closingDate) return false;
    const daysUntilClosing = Math.ceil((new Date(closingDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilClosing <= 7 && daysUntilClosing >= 0;
  };

  const isClosed = (closingDate: string) => {
    if (!closingDate) return false;
    return new Date(closingDate) < new Date();
  };

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ 
            color: '#00579a', 
            fontWeight: 600,
            fontSize: '28px',
            margin: 0
          }}>
            Vacancies Management
          </h1>
          <Link 
            to="/vacancies/new" 
            className="btn"
            style={{
              backgroundColor: '#00579a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 20px',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#004a85'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#00579a'}
          >
            + Add New Vacancy
          </Link>
        </div>

        {error && (
          <Alert 
            variant="danger" 
            dismissible 
            onClose={() => setError('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0'
            }}
          >
            {error}
          </Alert>
        )}

        <Card style={{ 
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          overflow: 'hidden'
        }}>
          <Card.Body style={{ padding: 0 }}>
            {loading ? (
              <div className="text-center" style={{ padding: '60px 0' }}>
                <Spinner 
                  animation="border" 
                  style={{ color: '#00579a', width: '40px', height: '40px' }}
                />
                <p style={{ marginTop: '16px', color: '#8c8c8c' }}>Loading vacancies...</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table 
                  hover 
                  responsive 
                  style={{ 
                    margin: 0,
                    borderCollapse: 'separate',
                    borderSpacing: 0
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#fafafa' }}>
                      <th style={headerStyle}>ID</th>
                      <th style={headerStyle}>Title</th>
                      <th style={headerStyle}>Closing Date</th>
                      <th style={headerStyle}>Status</th>
                      <th style={headerStyle}>Documents</th>
                      <th style={headerStyle}>Created</th>
                      <th style={headerStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vacancies.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center" style={{ 
                          padding: '60px 20px',
                          color: '#8c8c8c',
                          fontSize: '15px'
                        }}>
                          No vacancies found. Click "Add New Vacancy" to create one.
                        </td>
                      </tr>
                    ) : (
                      vacancies.map((vacancy) => (
                        <tr 
                          key={vacancy.id}
                          style={{
                            transition: 'background-color 0.2s ease'
                          }}
                        >
                          <td style={cellStyle}>
                            <Badge 
                              bg="light" 
                              text="dark"
                              style={{
                                fontWeight: 500,
                                padding: '4px 8px'
                              }}
                            >
                              #{vacancy.id}
                            </Badge>
                          </td>
                          <td style={{ ...cellStyle, fontWeight: 500, color: '#262626' }}>
                            {vacancy.title}
                          </td>
                          <td style={cellStyle}>
                            <div>
                              {formatDate(vacancy.closing_date)}
                              {isClosingSoon(vacancy.closing_date) && (
                                <Badge 
                                  style={{
                                    backgroundColor: '#fff7e6',
                                    color: '#d46b08',
                                    fontWeight: 500,
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    marginLeft: '8px'
                                  }}
                                >
                                  Closing Soon
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td style={cellStyle}>
                            {isClosed(vacancy.closing_date) ? (
                              <Badge 
                                style={{
                                  backgroundColor: '#fff1f0',
                                  color: '#ffffffff',
                                  fontWeight: 500,
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}
                              >
                                Closed
                              </Badge>
                            ) : (
                              <Badge 
                                style={{
                                  backgroundColor: '#f6ffed',
                                  color: '#ffffffff',
                                  fontWeight: 500,
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}
                              >
                                Open
                              </Badge>
                            )}
                          </td>
                          <td style={cellStyle}>
                            <Badge 
                              bg="secondary"
                              style={{
                                backgroundColor: '#f0f0f0',
                                color: '#000000ff',
                                fontWeight: 500,
                                padding: '4px 10px',
                                borderRadius: '4px'
                              }}
                            >
                              {vacancy.documents?.length || 0} files
                            </Badge>
                          </td>
                          <td style={cellStyle}>{formatDate(vacancy.created_at)}</td>
                          <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                            <Link 
                              to={`/vacancies/edit/${vacancy.id}`} 
                              className="btn btn-sm me-2"
                              style={{
                                backgroundColor: 'white',
                                color: '#00579a',
                                border: '1px solid #00579a',
                                borderRadius: '4px',
                                padding: '4px 12px',
                                fontSize: '13px',
                                fontWeight: 500,
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#e6f4ff';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                              }}
                            >
                              Edit
                            </Link>
                            <Button 
                              size="sm" 
                              onClick={() => handleDelete(vacancy.id)}
                              style={{
                                backgroundColor: 'white',
                                color: '#ff4d4f',
                                border: '1px solid #ff4d4f',
                                borderRadius: '4px',
                                padding: '4px 12px',
                                fontSize: '13px',
                                fontWeight: 500,
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#fff1f0';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                              }}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

const headerStyle: React.CSSProperties = {
  padding: '16px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#595959',
  borderBottom: '1px solid #f0f0f0',
  backgroundColor: '#fafafa',
  whiteSpace: 'nowrap'
};

const cellStyle: React.CSSProperties = {
  padding: '16px',
  fontSize: '14px',
  color: '#595959',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle'
};

export default Vacancies;